import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import MeetingRecorder from "@/components/MeetingRecorder";
import StakeholderDigestDialog from "@/components/StakeholderDigestDialog";
import OrganizedNoteBody from "@/components/OrganizedNoteBody";
import AIContextPicker from "@/components/AIContextPicker";
import RelatedMeetingNotes from "@/components/RelatedMeetingNotes";
import MeetingNoteActivityEditor from "@/components/MeetingNoteActivityEditor";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Sparkle, Translate, ListChecks, CaretDown, CaretRight, UsersThree, PencilSimple, Check, X, PaperPlaneTilt, ArrowSquareOut, DotsThreeVertical, CalendarStar } from "@phosphor-icons/react";
import { entryText, keyPointParts } from "@/lib/structuredEntry";

const LANG_LABEL = { en: "English", he: "עברית", es: "Español", ar: "العربية" };
const RTL = new Set(["he", "ar"]);

/** Meeting-note detail: recorder, transcript, AI structured output, translations,
 * and one-click promotion of action items into real Tasks.
 *
 * Iter 85.43 · `variant` controls the surface density:
 *   - `variant="full"` (default) — recorder + audio + toolbar + transcript +
 *     structured body. Mounted inside the Note Viewer where the partner
 *     works on the full record.
 *   - `variant="compact"` — the toolbar row only (Organize/Re-organize,
 *     Promote action items → Tasks, Post brief to engagement, Send
 *     stakeholder digest, Diarize speakers, language chips). Recorder /
 *     audio player / transcript / structured body are skipped so the
 *     Advisory Desk collapsed card can expose the working actions
 *     without dragging in the full editor. The buttons call the exact
 *     same backend endpoints (`/notes/{id}/organize`, `/notes/{id}/
 *     promote-actions`, `/notes/{id}/post-brief`, `/notes/{id}/send-
 *     digest`) as the full variant — zero functional duplication.
 */
export default function MeetingNoteCard({ note, onUpdated, variant = "full" }) {
  const isCompact = variant === "compact";
  const [current, setCurrent] = useState(note);
  const [organizing, setOrganizing] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [lang, setLang] = useState("en");   // display language
  const [showTranscript, setShowTranscript] = useState(false);
  const [diarizing, setDiarizing] = useState(false);
  const [renamingSpeaker, setRenamingSpeaker] = useState(null);  // {from} in-progress rename
  const [renameValue, setRenameValue] = useState("");
  const [postingBrief, setPostingBrief] = useState(false);
  const [digestOpen, setDigestOpen] = useState(false);
  // Iter 85.70 · Version-aware Post-to-Engagement.
  //   posted=false                       → "Post to Engagement"
  //   posted=true, has_updates=true      → "Post Updated Version"
  //   posted=true, has_updates=false     → "Posted to Engagement" (manual re-post allowed)
  const [postStatus, setPostStatus] = useState(() => ({
    posted: Boolean(note?.brief_posted_note_id),
    has_updates: false,
    version: Number(note?.brief_posted_count || 0),
    posted_at: note?.brief_posted_at || null,
    first_posted_at: note?.brief_first_posted_at || note?.brief_posted_at || null,
    brief_id: note?.brief_posted_note_id || null,
  }));

  const diarize = async () => {
    setDiarizing(true);
    try {
      const { data } = await api.post(`/notes/${current.id}/diarize`);
      setCurrent({ ...current, meeting_diarized: data.diarized });
      toast.success(`Diarized into ${data.diarized?.speakers?.length || "?"} speakers`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Diarization failed");
    } finally { setDiarizing(false); }
  };

  const fmtTs = (secs) => {
    const s = Math.floor(secs || 0);
    return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
  };
  const jumpTo = (secs) => {
    const audio = document.querySelector(`[data-testid="meeting-audio-player"]`);
    if (audio && secs != null) {
      audio.currentTime = secs;
      audio.play?.().catch(() => {});
    }
  };

  const renameSpeaker = async (fromLabel, toName) => {
    if (!toName || toName === fromLabel) { setRenamingSpeaker(null); return; }
    try {
      const { data } = await api.post(`/notes/${current.id}/diarize/rename`, {
        from_label: fromLabel,
        to_name: toName,
        add_to_profile: true,
      });
      setCurrent({ ...current, meeting_diarized: data.diarized });
      toast.success(`Renamed to "${toName}" · saved to voice profile so future meetings auto-label`);
      setRenamingSpeaker(null); setRenameValue("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rename failed");
    }
  };

  const refreshPostStatus = async (noteId = current.id) => {
    if (!noteId) return null;
    try {
      const { data } = await api.get(`/notes/${noteId}/post-status`);
      setPostStatus(data);
      return data;
    } catch (e) {
      // Silent — status is a UX affordance, not critical.
      return null;
    }
  };

  // Fetch fresh post-status once the card mounts / when the note swap-in
  // completes. Keeps the button state in sync even if the note was
  // organized in another surface before landing here.
  useEffect(() => {
    if (current?.id && current?.engagement_id) refreshPostStatus(current.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.id]);

  const postBrief = async () => {
    setPostingBrief(true);
    try {
      const { data } = await api.post(`/notes/${current.id}/post-to-engagement`);
      // Iter 85.85 · Explicit "brief now current" affirmation so
      // advisors know their edits landed in the exportable brief
      // without opening it. The backend already refreshes
      // `brief_posted_source_hash` so the button flips out of the
      // "Post Updated Version" state on next status poll.
      let label;
      if (data?.was_update) {
        label = `Brief refreshed with your latest edits · v${data.version}`;
      } else {
        label = "Meeting brief posted to engagement";
      }
      toast.success(`${label} · ${data.brief?.title || ""}`.trim());
      setCurrent({
        ...current,
        brief_posted_note_id: data.brief?.id,
        brief_posted_count: data.version,
        brief_posted_at: data.posted_at,
      });
      // Refresh status so has_updates flips back to false immediately.
      await refreshPostStatus(current.id);
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Post brief failed");
    } finally { setPostingBrief(false); }
  };

  const refresh = async () => {
    const { data } = await api.get(`/notes/${current.id}`).catch(() => ({ data: null }));
    if (data) setCurrent(data);
    onUpdated?.();
  };

  const handleTranscribed = (data) => {
    setCurrent({ ...current, ...data });
    onUpdated?.();
  };

  // Iter 85.16 · Pre-flight context picker state — attachments + prior
  // notes to include in the Organize payload. Reset when the current
  // note changes.
  const [pickedAttachments, setPickedAttachments] = useState(() => new Set());
  const [pickedPriorNotes, setPickedPriorNotes] = useState(() => new Set());
  useEffect(() => {
    setPickedAttachments(new Set());
    setPickedPriorNotes(new Set());
  }, [current?.id]);

  const organize = async ({ silent = false } = {}) => {
    setOrganizing(true);
    try {
      const contextPayload = (pickedAttachments.size || pickedPriorNotes.size)
        ? { context: { attachment_ids: [...pickedAttachments], prior_note_ids: [...pickedPriorNotes] } }
        : {};
      const { data } = await api.post(`/notes/${current.id}/organize`, contextPayload);
      const next = { ...current, meeting_structured: data.meeting_structured, meeting_organized_at: data.meeting_organized_at };
      setCurrent(next);
      // Iter 85.47 · Propagate the organized result to the parent so
      // the Advisory Desk feed preview switches from raw body to the
      // organized summary. Without this, `setCurrent` only updates
      // THIS card's local state — the feed card's preview in Notes.jsx
      // reads from the parent's `notes` state and would keep showing
      // the raw body indefinitely, even though the DB is up-to-date.
      onUpdated?.();
      // Iter 85.70 · Re-organize changes the source signature → refresh
      // post-status so the button flips to "Post Updated Version" when a
      // brief was already posted.
      if (current.engagement_id) refreshPostStatus(current.id);
      if (!silent) {
        const extras = pickedAttachments.size + pickedPriorNotes.size;
        toast.success(
          extras
            ? `Organized with ${extras} extra context item${extras === 1 ? "" : "s"}`
            : "Meeting organized into decisions, actions and follow-ups"
        );
      }
      return next;
    } catch (e) {
      if (!silent) toast.error(e?.response?.data?.detail || "Organize failed");
      throw e;
    } finally { setOrganizing(false); }
  };

  const translateTo = async (targetLang) => {
    if (targetLang === "en") { setLang("en"); return; }
    // Serve from cache if present.
    if ((current.meeting_translations || {})[targetLang]) { setLang(targetLang); return; }
    setTranslating(true);
    try {
      const { data } = await api.post(`/notes/${current.id}/translate`, null, { params: { to: targetLang } });
      setCurrent({
        ...current,
        meeting_translations: { ...(current.meeting_translations || {}), [targetLang]: data.translation },
      });
      setLang(targetLang);
      toast.success(`Translated to ${LANG_LABEL[targetLang]}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Translation failed");
    } finally { setTranslating(false); }
  };

  const promoteActions = async () => {
    setPromoting(true);
    try {
      // If the note has no action items yet (either never organized, or organized
      // to an empty list), transparently run Organize first so the partner never
      // has to think about the two-step. This turns the toast-driven error into
      // a single-click experience.
      const items = current.meeting_structured?.action_items || [];
      if (items.length === 0) {
        toast.info("No action items yet — organizing the meeting first…");
        const fresh = await organize({ silent: true });
        const freshItems = fresh?.meeting_structured?.action_items || [];
        if (freshItems.length === 0) {
          toast.info("Organizer found no action items in this meeting.");
          return;
        }
      }
      const { data } = await api.post(`/notes/${current.id}/promote-actions`);
      // Iter 85.10 · Bulk endpoint now reuses the canonical single-item
      // promotion helper, so it returns the refreshed action_items with
      // `promoted_task_id` stamped on every eligible row. Hydrate local
      // state so the per-item "Promote to Task" chips flip to their
      // "Task ↗" linked state immediately, without a reload.
      if (data?.note?.meeting_structured?.action_items) {
        setCurrent((c) => ({
          ...c,
          meeting_structured: {
            ...(c.meeting_structured || {}),
            action_items: data.note.meeting_structured.action_items,
          },
        }));
      }
      const created = data?.created_count ?? 0;
      const skipped = data?.skipped_count ?? 0;
      if (created > 0 && skipped > 0) {
        toast.success(`Promoted ${created} action item${created === 1 ? "" : "s"} · ${skipped} already linked`);
      } else if (created > 0) {
        toast.success(`Promoted ${created} action item${created === 1 ? "" : "s"} to Tasks`);
      } else if (skipped > 0) {
        toast.info(`All ${skipped} action item${skipped === 1 ? "" : "s"} already linked to Tasks`);
      } else {
        toast.info("No action items to promote.");
      }
    } catch (e) {
      if (e?.response) toast.error(e?.response?.data?.detail || "Promote failed");
    } finally { setPromoting(false); }
  };

  // Iter 85.89 · Promote this Meeting note into a new canonical Activity
  // Briefing (or open the linked one if already promoted). Preserves
  // provenance — no Tasks/Decisions are cloned; they surface under the
  // Activity via `activity_briefing_id` lineage.
  const [promotingActivity, setPromotingActivity] = useState(false);
  const promoteToActivityBriefing = async () => {
    setPromotingActivity(true);
    try {
      const { data } = await api.post(`/notes/${current.id}/promote-to-activity-briefing`);
      const activityId = data?.activity?.id;
      if (data?.already_promoted && data?.synced) {
        toast.success("Activity Briefing synced with organized content.");
      } else if (data?.already_promoted) {
        toast.info("Activity Briefing already in sync.");
      } else {
        toast.success("Promoted to Activity Briefing.");
      }
      // Stamp the source note's local state so the Tools menu flips to
      // "Open Activity Briefing" without needing a refetch.
      if (activityId) {
        setCurrent((c) => ({ ...c, activity_briefing_id: activityId }));
        onUpdated?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promote to Activity Briefing failed");
    } finally { setPromotingActivity(false); }
  };

  const hasAudio = !!current.meeting_audio_key;
  const hasTranscript = !!current.meeting_transcript;
  const hasStructured = !!current.meeting_structured;
  // Iter 85.15 · Toolbar gate widened from `hasTranscript` alone. A
  // typed briefing note has no transcript but is still legitimate
  // meeting content — Organize / Promote-to-Tasks / Send digest all
  // work against `meeting_structured` regardless of source. `hasBody`
  // filters out the auto-created placeholder body so the Organize
  // button doesn't appear on empty stubs.
  const bodyText = (current.body || "").trim();
  const hasBody = !!bodyText && bodyText !== "(Briefing draft — record or paste material)" && bodyText !== "(Recorded meeting)";
  const canOrganize = hasTranscript || hasBody;
  const showToolbar = canOrganize || hasStructured;
  const structuredDisplay = lang === "en"
    ? current.meeting_structured
    : (current.meeting_translations || {})[lang]?.structured;
  const transcriptDisplay = lang === "en"
    ? current.meeting_transcript
    : (current.meeting_translations || {})[lang]?.transcript;
  const isRTL = RTL.has(lang);

  return (
    <div
      className={isCompact ? "space-y-3" : "border border-border bg-secondary/20 p-4 mt-3 space-y-4"}
      data-testid={`meeting-note-card-${current.id}`}
    >
      {/* Recorder — surfaced on BOTH variants so partners can start
          capturing audio directly from the Advisory Desk collapsed card
          without opening the record. Iter 85.44. */}
      {!hasAudio && <MeetingRecorder noteId={current.id} onTranscribed={handleTranscribed} />}

      {/* Audio playback + metadata — kept on the collapsed card too so
          the advisor can review existing recordings inline. */}
      {hasAudio && (
        <div className="border border-border bg-card p-3 flex items-center gap-3 flex-wrap">
          <audio
            controls
            src={`${process.env.REACT_APP_BACKEND_URL}/api/notes/${current.id}/audio-stream?_=${current.meeting_transcribed_at || ""}`}
            className="max-w-full flex-1 min-w-[220px]"
            data-testid="meeting-audio-player"
          />
          <div className="text-[0.625rem] font-mono text-muted-foreground uppercase tracking-widest">
            {current.meeting_transcript_lang || "?"} · {current.meeting_audio_duration_seconds ? Math.round(current.meeting_audio_duration_seconds) + "s" : "—"} · auto-deletes {current.meeting_audio_expires_at ? new Date(current.meeting_audio_expires_at).toLocaleDateString() : "in 30d"}
          </div>
        </div>
      )}

      {/* Actions row */}
      {showToolbar && (
        <div className="space-y-3">
          {/* Iter 85.16 · Pre-flight Context Picker. Only offered when
              the note has some content to organize (canOrganize) —
              otherwise the picker toggles nothing useful. Skipped in
              the compact Advisory Desk toolbar to keep the collapsed
              card tight; partners access it via Open → full viewer. */}
          {canOrganize && !isCompact && (
            <AIContextPicker
              noteId={current.id}
              pickedAttachments={pickedAttachments}
              setPickedAttachments={setPickedAttachments}
              pickedPriorNotes={pickedPriorNotes}
              setPickedPriorNotes={setPickedPriorNotes}
              testIdPrefix="organize-context"
              onAttachmentUploaded={(a) => {
                // Iter 85.30 · Sync the fresh attachment into the card's
                // local note state so downstream badges/menus see it
                // immediately.
                setCurrent((c) => ({ ...c, attachments: [...(c.attachments || []), a] }));
                onUpdated?.();
              }}
            />
          )}
          {/* Iter 85.89.1 · Symmetrical Tools bar.
              ─────────────────────────────────
              Two visually distinct rows so status never masquerades as
              action:
                Row 1 · Status strip  — thin, read-only chips (posted vX,
                        actions promoted, activity linked). Auto-hides
                        when empty so the toolbar stays tight on a fresh
                        meeting.
                Row 2 · Action rail   — every button shares uniform sizing
                        (h-10 px-4 font-medium) for symmetry. The
                        `Actions` menu is BRASS-accented so it stands out
                        as the discoverable entry-point for secondary
                        tools. Language segmented control right-aligned. */}
          {(() => {
            const items = current.meeting_structured?.action_items || [];
            const unpromoted = items.filter((a) => !a?.promoted_task_id).length;
            const allPromoted = items.length > 0 && unpromoted === 0;
            const posted = postStatus.posted;
            const hasUpdates = postStatus.has_updates;
            const version = postStatus.version;
            const briefId = postStatus.brief_id;
            const canPromoteToActivity =
              current.note_type === "meeting" && !current.activity_briefing_id;
            const alreadyLinked =
              current.note_type === "meeting" && !!current.activity_briefing_id;
            const canSyncToActivity = alreadyLinked && hasStructured;
            const canDiarize = !current.meeting_diarized && (current.meeting_transcript_segments?.length || 0) > 0;
            const hasOverflowActions = hasStructured || canPromoteToActivity || canSyncToActivity || canDiarize;
            const hasStatus = (posted && briefId) || (hasStructured && allPromoted) || alreadyLinked;
            // Shared classnames — uniform sizing for symmetry across
            // every primary action button.
            const BTN_BASE = "rounded-none h-10 px-4 min-w-[9rem] justify-center font-medium text-sm focus-visible:ring-2";
            return (
              <div className="space-y-3" data-testid="meeting-tools-bar">
                {/* ── ROW 1 · STATUS STRIP ──────────────────────── */}
                {hasStatus && (
                  <div
                    className="flex flex-wrap items-center gap-2"
                    data-testid="meeting-tools-status-strip"
                    role="status"
                    aria-label="Meeting status"
                  >
                    {posted && briefId && (
                      <Link
                        to={`/notes?open=${briefId}`}
                        data-testid="meeting-brief-open-link"
                        className="inline-flex items-center gap-1 border border-emerald-500/40 bg-emerald-500/5 text-emerald-500 text-[0.625rem] font-mono uppercase tracking-widest px-2 py-1 hover:bg-emerald-500/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/50"
                        title={`Brief v${version} · posted ${postStatus.posted_at ? new Date(postStatus.posted_at).toLocaleString() : ""}`}
                      >
                        <PaperPlaneTilt size={11} weight="fill" /> Brief posted · v{version}
                      </Link>
                    )}
                    {hasStructured && allPromoted && (
                      <span
                        data-testid="meeting-all-promoted-chip"
                        className="inline-flex items-center gap-1 border border-emerald-500/40 bg-emerald-500/5 text-emerald-500 text-[0.625rem] font-mono uppercase tracking-widest px-2 py-1"
                        title="Every action item is linked to a Task"
                      >
                        <Check size={11} weight="bold" /> Actions promoted
                      </span>
                    )}
                    {alreadyLinked && (
                      <Link
                        to={`/notes?open=${current.activity_briefing_id}`}
                        data-testid="meeting-activity-link-chip"
                        className="inline-flex items-center gap-1 border border-brass/40 bg-brass-soft/5 text-brass text-[0.625rem] font-mono uppercase tracking-widest px-2 py-1 hover:bg-brass-soft/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brass/50"
                        title="This meeting belongs to an Activity Briefing"
                      >
                        <CalendarStar size={11} weight="fill" /> Activity linked
                      </Link>
                    )}
                  </div>
                )}

                {/* ── ROW 2 · ACTION RAIL ───────────────────────── */}
                <div className="flex flex-wrap items-center gap-2">
                  {canOrganize && (
                    <Button
                      data-testid="meeting-organize-btn"
                      onClick={() => organize()}
                      disabled={organizing}
                      className={`${BTN_BASE} focus-visible:ring-primary/60 ${hasStructured
                        ? "border border-primary/60 bg-transparent text-primary hover:bg-primary/10"
                        : "bg-primary text-primary-foreground hover:bg-primary/90"}`}
                    >
                      <Sparkle size={14} weight="fill" className="mr-1.5" />
                      {organizing ? "Organizing…" : hasStructured ? "Re-organize" : "Organize with AI"}
                      {(pickedAttachments.size || pickedPriorNotes.size) ? (
                        <span className="ml-1.5 opacity-90 text-[0.6875rem]">
                          {" · +"}{pickedAttachments.size + pickedPriorNotes.size}
                        </span>
                      ) : null}
                    </Button>
                  )}
                  {hasStructured && current.engagement_id && (() => {
                    let label, testid, isPrimary;
                    if (!posted) {
                      label = postingBrief ? "Posting…" : "Post to Engagement";
                      testid = "meeting-post-brief-btn";
                      isPrimary = true;
                    } else if (hasUpdates) {
                      label = postingBrief ? "Posting update…" : "Post Updated Version";
                      testid = "meeting-post-updated-btn";
                      isPrimary = true;
                    } else {
                      label = postingBrief ? "Re-posting…" : "Re-post to Engagement";
                      testid = "meeting-brief-posted-btn";
                      isPrimary = false;
                    }
                    const className = isPrimary
                      ? `${BTN_BASE} focus-visible:ring-primary/60 border border-primary/60 bg-transparent text-primary hover:bg-primary/10`
                      : `${BTN_BASE} focus-visible:ring-emerald-500/60 border border-emerald-500/40 bg-transparent text-emerald-500 hover:bg-emerald-500/10`;
                    return (
                      <Button
                        data-testid={testid}
                        onClick={postBrief}
                        disabled={postingBrief}
                        variant="outline"
                        className={className}
                        title={posted && !hasUpdates ? "Manually re-post to refresh the engagement brief" : (hasUpdates ? "Meeting has been re-organized — refresh the engagement brief" : "Publish this meeting's brief to the linked engagement")}
                      >
                        {posted && !hasUpdates ? <Check size={14} weight="bold" className="mr-1.5" /> : <PaperPlaneTilt size={14} weight="bold" className="mr-1.5" />}
                        {label}
                      </Button>
                    );
                  })()}

                  {/* ── MORE ACTIONS — brass-accented so it stands out
                        as the discoverable overflow trigger. */}
                  {hasOverflowActions && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          data-testid="meeting-tools-menu-trigger"
                          className={`${BTN_BASE} focus-visible:ring-brass/60 border-2 border-brass/60 bg-brass-soft/5 text-brass hover:bg-brass-soft/15 hover:border-brass shadow-[0_0_0_1px_rgba(0,0,0,0)]`}
                          title="More actions"
                          aria-label="More actions for this meeting note"
                        >
                          <DotsThreeVertical size={16} weight="bold" className="mr-1" />
                          Actions
                          <CaretDown size={12} weight="bold" className="ml-1.5 opacity-70" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start" className="rounded-none min-w-[280px] border-brass/40" data-testid="meeting-tools-menu">
                        <DropdownMenuLabel className="text-[0.625rem] font-mono uppercase tracking-widest text-brass">
                          Meeting tools
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {hasStructured && (
                          <DropdownMenuItem
                            data-testid="meeting-promote-actions-btn"
                            disabled={promoting || organizing || allPromoted}
                            onSelect={(e) => { e.preventDefault(); if (!promoting && !organizing && !allPromoted) promoteActions(); }}
                            className="py-2.5 gap-2 cursor-pointer"
                          >
                            <ListChecks size={14} weight="bold" className="text-primary shrink-0" />
                            <span className="flex-1 text-sm">
                              {promoting
                                ? "Promoting…"
                                : allPromoted
                                  ? "Action items already promoted"
                                  : `Promote action items → Tasks${unpromoted > 0 && unpromoted < items.length ? ` (${unpromoted})` : ""}`}
                            </span>
                          </DropdownMenuItem>
                        )}
                        {canPromoteToActivity && (
                          <DropdownMenuItem
                            data-testid="meeting-promote-to-activity-btn"
                            disabled={promotingActivity}
                            onSelect={(e) => { e.preventDefault(); if (!promotingActivity) promoteToActivityBriefing(); }}
                            className="py-2.5 gap-2 cursor-pointer"
                          >
                            <CalendarStar size={14} weight="fill" className="text-brass shrink-0" />
                            <span className="flex-1 text-sm">
                              {promotingActivity ? "Promoting…" : "Promote to Activity Briefing"}
                            </span>
                          </DropdownMenuItem>
                        )}
                        {alreadyLinked && hasStructured && (
                          <DropdownMenuItem
                            data-testid="meeting-sync-to-activity-btn"
                            disabled={promotingActivity}
                            onSelect={(e) => { e.preventDefault(); if (!promotingActivity) promoteToActivityBriefing(); }}
                            className="py-2.5 gap-2 cursor-pointer"
                            title="Copy this meeting's organized content into the linked Activity Briefing (only fills empty fields — never overwrites edits)"
                          >
                            <CalendarStar size={14} weight="fill" className="text-brass shrink-0" />
                            <span className="flex-1 text-sm">
                              {promotingActivity ? "Syncing…" : "Sync organized content to Activity"}
                            </span>
                          </DropdownMenuItem>
                        )}
                        {hasStructured && (
                          <DropdownMenuItem
                            data-testid="meeting-send-digest-btn"
                            onSelect={(e) => { e.preventDefault(); setDigestOpen(true); }}
                            className="py-2.5 gap-2 cursor-pointer"
                          >
                            <PaperPlaneTilt size={14} weight="bold" className="text-primary shrink-0" />
                            <span className="flex-1 text-sm">
                              {current.stakeholder_digests?.length > 0 ? "Resend stakeholder digest" : "Send stakeholder digest"}
                            </span>
                          </DropdownMenuItem>
                        )}
                        {canDiarize && (
                          <DropdownMenuItem
                            data-testid="meeting-diarize-btn"
                            disabled={diarizing}
                            onSelect={(e) => { e.preventDefault(); if (!diarizing) diarize(); }}
                            className="py-2.5 gap-2 cursor-pointer"
                          >
                            <UsersThree size={14} weight="bold" className="text-muted-foreground shrink-0" />
                            <span className="flex-1 text-sm">
                              {diarizing ? "Labelling speakers…" : "Diarize speakers"}
                            </span>
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}

                  {/* ── LANGUAGE (compact icon-only trigger + dropdown)
                        Iter 85.92 · Reclaims the horizontal space the
                        segmented control was hogging so the toolbar
                        never overlaps at narrower viewports. Renders
                        the translate glyph alone by default; the
                        active language surfaces as a small badge
                        beneath the icon so advisors keep at-a-glance
                        awareness of the current view mode. */}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        data-testid="meeting-language-trigger"
                        disabled={translating}
                        title={`Display language: ${LANG_LABEL[lang] || "English"}. Click to switch.`}
                        aria-label={`Display language: ${LANG_LABEL[lang] || "English"}. Click to switch.`}
                        className="ml-auto h-10 w-10 border border-border text-muted-foreground hover:text-primary hover:border-primary/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 transition-colors inline-flex items-center justify-center relative"
                      >
                        <Translate size={16} weight="bold" />
                        {lang && lang !== "en" && (
                          <span
                            aria-hidden="true"
                            className="absolute -bottom-1 -right-1 text-[0.5rem] font-mono uppercase leading-none px-1 py-[1px] bg-primary text-primary-foreground"
                          >
                            {lang}
                          </span>
                        )}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="rounded-none min-w-[180px]" data-testid="meeting-language-menu">
                      <DropdownMenuLabel className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground">
                        Display language
                      </DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {["en", "he", "es", "ar"].map((l) => (
                        <DropdownMenuItem
                          key={l}
                          data-testid={`meeting-lang-${l}`}
                          disabled={translating}
                          onSelect={(e) => { e.preventDefault(); if (!translating) translateTo(l); }}
                          className="py-2 gap-2 cursor-pointer justify-between"
                          aria-pressed={lang === l}
                        >
                          <span className={`text-sm ${lang === l ? "text-primary font-medium" : ""}`}>
                            {LANG_LABEL[l]}
                          </span>
                          {lang === l && <Check size={12} weight="bold" className="text-primary" />}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Iter 85.44 · Relationship & workflow context migrated from the
          Note Viewer Open modal so the Advisory Desk collapsed card
          owns all working tools + relationship navigation. Rendered
          exclusively in compact mode (variant="compact"). */}
      {isCompact && current.note_type === "activity_briefing" && (
        <>
          <div
            className="border border-brass/30 bg-brass-soft/10 px-3 py-2 flex flex-wrap items-center justify-between gap-3"
            data-testid="activity-followup-strip"
          >
            <div className="text-[0.6875rem] font-mono uppercase tracking-widest text-brass">
              Post-event debrief — one tap
            </div>
            <Link
              to={`/notes?followup=${current.id}`}
              className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:opacity-80"
              data-testid="activity-followup-cta"
            >
              Start follow-up debrief <ArrowSquareOut size={12} />
            </Link>
          </div>
          <RelatedMeetingNotes activityId={current.id} />
        </>
      )}
      {isCompact && current.note_type !== "activity_briefing" && (
        <MeetingNoteActivityEditor note={current} onNoteChange={(next) => { setCurrent(next); onUpdated?.(); }} />
      )}

      {/* Structured output.
          Iter 80 · In English (source-of-truth) mode we render the shared
          `OrganizedNoteBody` — the exact same interactive Action Items,
          Decisions, Key Points, Open Questions, Follow-ups and Suggested
          Engagements that NoteViewerDialog uses. Completion toggle,
          Promote-to-Task, inline owner/due edit, and Spawn-briefing all
          work in place, so a partner never has to pop the modal to act
          on a meeting note.
          For non-English translations we keep a read-only Section-based
          rendering because interactions would edit the canonical
          English record while showing translated copy — confusing UX. */}
      {hasStructured && lang === "en" && !isCompact && (
        <div className="border border-border bg-card p-4 text-sm" data-testid="meeting-structured">
          <OrganizedNoteBody note={current} onNoteChange={(next) => { setCurrent(next); onUpdated?.(); }} />
        </div>
      )}
      {hasStructured && lang !== "en" && structuredDisplay && (
        <div dir={isRTL ? "rtl" : "ltr"} className="border border-border bg-card p-4 space-y-4 text-sm" data-testid="meeting-structured-translated">
          <Section title="Summary">{structuredDisplay.summary}</Section>
          {structuredDisplay.decisions?.length > 0 && (
            <Section title="Decisions">
              <ul className="list-disc pl-5 space-y-1">
                {structuredDisplay.decisions.map((d, i) => <li key={i}>{entryText(d)}</li>)}
              </ul>
            </Section>
          )}
          {structuredDisplay.action_items?.length > 0 && (
            <Section title="Action items">
              <ul className="space-y-1.5">
                {structuredDisplay.action_items.map((a, i) => (
                  <li key={i} className="border-l-2 border-primary/50 pl-3">
                    <div className="text-sm">{a.task}</div>
                    <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">
                      {a.owner || "unassigned"} · due {a.due || "—"}
                    </div>
                  </li>
                ))}
              </ul>
            </Section>
          )}
          {structuredDisplay.key_points?.length > 0 && (
            <Section title="Key points">
              <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                {structuredDisplay.key_points.map((k, i) => {
                  const { title, body } = keyPointParts(k);
                  return (
                    <li key={i}>
                      {title ? <><span className="font-semibold text-foreground">{title}</span>{body ? " — " : ""}</> : null}
                      {body}
                    </li>
                  );
                })}
              </ul>
            </Section>
          )}
          {structuredDisplay.open_questions?.length > 0 && (
            <Section title="Open questions">
              <ul className="list-disc pl-5 space-y-1">
                {structuredDisplay.open_questions.map((q, i) => <li key={i}>{entryText(q)}</li>)}
              </ul>
            </Section>
          )}
          {structuredDisplay.follow_ups?.length > 0 && (
            <Section title="Follow-ups">
              <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
                {structuredDisplay.follow_ups.map((f, i) => <li key={i}>{entryText(f)}</li>)}
              </ul>
            </Section>
          )}
        </div>
      )}

      {/* Diarized speaker turns — full variant only */}
      {!isCompact && current.meeting_diarized?.turns?.length > 0 && (
        <div className="border border-border bg-card p-4" data-testid="meeting-diarized">
          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow mb-2 flex items-center gap-2">
            <UsersThree size={11} weight="bold" /> Diarized transcript · {current.meeting_diarized.speakers?.length || 0} speakers · {current.meeting_diarized.confidence || "?"} confidence
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {current.meeting_diarized.turns.map((t, i) => (
              <div key={(t.start || 0) + "-" + i} className="flex gap-3 text-sm" data-testid={`meeting-turn-${i}`}>
                <button
                  onClick={() => jumpTo(t.start)}
                  disabled={!hasAudio}
                  title={hasAudio ? `Jump to ${fmtTs(t.start)}` : "Audio has been purged"}
                  className={`shrink-0 w-14 text-[0.625rem] font-mono ${hasAudio ? "text-primary hover:underline" : "text-muted-foreground"} tabular-nums`}
                >{fmtTs(t.start)}</button>
                <div className="flex-1 min-w-0">
                  <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
                    {renamingSpeaker === t.speaker ? (
                      <>
                        <input
                          data-testid={`speaker-rename-input-${i}`}
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") renameSpeaker(t.speaker, renameValue.trim()); if (e.key === "Escape") { setRenamingSpeaker(null); setRenameValue(""); } }}
                          autoFocus
                          placeholder="Real name (e.g. Jesse)"
                          className="bg-secondary/60 border border-primary/60 text-foreground px-1.5 py-0.5 text-[0.6875rem] w-32 normal-case tracking-normal font-sans"
                        />
                        <button onClick={() => renameSpeaker(t.speaker, renameValue.trim())} title="Save" data-testid={`speaker-rename-save-${i}`} className="text-emerald-500 hover:text-emerald-400"><Check size={11} weight="bold" /></button>
                        <button onClick={() => { setRenamingSpeaker(null); setRenameValue(""); }} title="Cancel" className="text-muted-foreground hover:text-destructive"><X size={11} /></button>
                      </>
                    ) : (
                      <>
                        <span>{t.speaker}</span>
                        <button
                          onClick={() => { setRenamingSpeaker(t.speaker); setRenameValue(t.speaker.startsWith("Speaker") ? "" : t.speaker); }}
                          data-testid={`speaker-rename-btn-${i}`}
                          title="Rename speaker and remember for future meetings"
                          className="text-muted-foreground hover:text-primary opacity-60 hover:opacity-100"
                        ><PencilSimple size={10} /></button>
                      </>
                    )}
                  </div>
                  <div className="text-sm leading-relaxed">{t.text}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Raw transcript (collapsed by default) — full variant only */}
      {!isCompact && hasTranscript && (
        <div className="border border-border bg-card">
          <button
            onClick={() => setShowTranscript((v) => !v)}
            data-testid="meeting-transcript-toggle"
            className="w-full px-3 py-2 text-left text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-eyebrow flex items-center gap-1"
          >
            {showTranscript ? <CaretDown size={11} /> : <CaretRight size={11} />} Raw transcript ({lang.toUpperCase()})
          </button>
          {showTranscript && (
            <div dir={isRTL ? "rtl" : "ltr"} className="px-3 pb-3 text-xs whitespace-pre-wrap leading-relaxed text-muted-foreground" data-testid="meeting-transcript-text">
              {transcriptDisplay || <em>Translation unavailable — click a language chip to translate.</em>}
            </div>
          )}
        </div>
      )}
      <StakeholderDigestDialog
        open={digestOpen}
        onClose={() => setDigestOpen(false)}
        note={current}
        onSent={(send) => setCurrent({ ...current, stakeholder_digests: [send, ...(current.stakeholder_digests || [])] })}
      />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow mb-1.5">{title}</div>
      <div>{children}</div>
    </div>
  );
}
